// STARWEAVE — visual/UX juice. Self-contained polish layer that complements the
// existing hyperdrive flash:
//   • a radial speed-streak "light tunnel" (THREE.LineSegments) that flares out
//     from the screen centre and fades over ~0.8s,
//   • a damped camera FOV "kick" that bumps the field-of-view on warp and eases
//     it back to the stored base,
//   • a "JUMP" label that punches in and fades,
//   • a themed Settings button (top-right) + panel: mute, reset save, controls.
//
// Owns its own DOM (button/panel/label) and THREE objects (the streak field).
// No edits to existing files — wire in via init() / triggerWarp() / update().

import * as THREE from 'three';

const ACCENT = '#ffb340';
const ACCENT_SOFT = '#ffd98a';

// ── Warp streak field ──────────────────────────────────────────────────────
// A radial burst of line segments anchored a fixed distance ahead of the camera.
// Each streak starts as a point and elongates outward along its random radial
// direction, scaling + fading as the warp pulse decays — a light-tunnel read.
const STREAKS = 220;

let camRef: THREE.PerspectiveCamera | null = null;
let baseFov = 55;

// Streak geometry: 2 verts per streak (inner + outer). Built once, re-anchored
// to the camera every frame while active.
const streakDir: THREE.Vector3[] = [];
const streakLen: number[] = [];
let streakGeo: THREE.BufferGeometry | null = null;
let streakSeg: THREE.LineSegments | null = null;
let streakMat: THREE.LineBasicMaterial | null = null;

// Warp animation state.
let warpT = 0;          // seconds remaining in the streak burst
const WARP_DUR = 0.8;
let fovKick = 0;        // current additive FOV bump (degrees), eased to 0

// DOM: the "JUMP" label, settings button + panel.
let jumpEl: HTMLDivElement | null = null;
let muted = false;

function buildStreaks(): void {
  const positions = new Float32Array(STREAKS * 2 * 3);
  const colors = new Float32Array(STREAKS * 2 * 3);
  const accent = new THREE.Color(ACCENT);
  const cool = new THREE.Color(0xeaf6ff);
  for (let i = 0; i < STREAKS; i++) {
    // Random direction biased toward the screen edges (sqrt for outward density).
    const ang = Math.random() * Math.PI * 2;
    const rad = Math.sqrt(Math.random());
    const dir = new THREE.Vector3(Math.cos(ang) * rad, Math.sin(ang) * rad, -1).normalize();
    streakDir.push(dir);
    streakLen.push(0.5 + Math.random() * 1.6);
    // Inner end cool-white, outer end amber accent → a warm light-tunnel.
    const c0 = cool, c1 = i % 3 === 0 ? accent : cool.clone().lerp(accent, 0.4);
    colors[i * 6 + 0] = c0.r; colors[i * 6 + 1] = c0.g; colors[i * 6 + 2] = c0.b;
    colors[i * 6 + 3] = c1.r; colors[i * 6 + 4] = c1.g; colors[i * 6 + 5] = c1.b;
  }
  streakGeo = new THREE.BufferGeometry();
  streakGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  streakGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  streakMat = new THREE.LineBasicMaterial({
    vertexColors: true, transparent: true, opacity: 0,
    blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
  });
  streakSeg = new THREE.LineSegments(streakGeo, streakMat);
  streakSeg.frustumCulled = false;
  streakSeg.renderOrder = 999;
  streakSeg.visible = false;
}

// Re-anchor the streak field in front of the camera and elongate by progress.
function updateStreaks(): void {
  if (!streakSeg || !streakGeo || !streakMat || !camRef) return;
  if (warpT <= 0) { streakSeg.visible = false; return; }
  streakSeg.visible = true;

  const cam = camRef;
  const fwd = new THREE.Vector3();
  cam.getWorldDirection(fwd);
  const right = new THREE.Vector3().crossVectors(fwd, cam.up).normalize();
  const up = new THREE.Vector3().crossVectors(right, fwd).normalize();

  // Progress 0→1 across the burst; ease so streaks shoot then settle.
  const p = 1 - warpT / WARP_DUR;
  const grow = 1 - Math.pow(1 - p, 2);       // outward elongation
  const fade = Math.sin(Math.min(1, p) * Math.PI); // in-then-out opacity

  // Anchor a short distance ahead so the field sits around the reticle and the
  // streaks stream past the camera (they live in world space at depthTest off).
  const anchorDist = 60;
  const center = new THREE.Vector3().copy(cam.position).addScaledVector(fwd, anchorDist);
  const baseSpread = 26;
  const reach = 220 * grow;

  const pos = streakGeo.attributes.position as THREE.BufferAttribute;
  const arr = pos.array as Float32Array;
  for (let i = 0; i < STREAKS; i++) {
    const d = streakDir[i];
    const len = streakLen[i];
    // Direction in camera-space → world-space.
    const wx = right.x * d.x + up.x * d.y + fwd.x * -d.z;
    const wy = right.y * d.x + up.y * d.y + fwd.y * -d.z;
    const wz = right.z * d.x + up.z * d.y + fwd.z * -d.z;
    const inner = baseSpread + reach * (0.35 + 0.65 * p);
    const outer = inner + len * (8 + reach * 0.55);
    arr[i * 6 + 0] = center.x + wx * inner;
    arr[i * 6 + 1] = center.y + wy * inner;
    arr[i * 6 + 2] = center.z + wz * inner;
    arr[i * 6 + 3] = center.x + wx * outer;
    arr[i * 6 + 4] = center.y + wy * outer;
    arr[i * 6 + 5] = center.z + wz * outer;
  }
  pos.needsUpdate = true;
  streakMat.opacity = 0.85 * fade;
}

// ── "JUMP" label ───────────────────────────────────────────────────────────
function buildJumpLabel(): void {
  jumpEl = document.createElement('div');
  jumpEl.textContent = 'JUMP';
  jumpEl.style.cssText = [
    'position:fixed', 'top:38%', 'left:50%', 'transform:translate(-50%,-50%) scale(0.7)',
    'pointer-events:none', 'z-index:45', 'opacity:0',
    "font-family:'Avenir Next Condensed','SF Pro Display',system-ui,sans-serif",
    'font-weight:600', 'font-size:46px', 'letter-spacing:14px', 'padding-left:14px',
    'text-transform:uppercase', `color:${ACCENT_SOFT}`,
    `text-shadow:0 0 22px rgba(255,179,64,0.85),0 0 6px rgba(255,217,138,0.9)`,
    'transition:opacity .18s ease-out, transform .5s cubic-bezier(.16,1,.3,1)',
  ].join(';');
  document.body.appendChild(jumpEl);
}

function flashJump(): void {
  if (!jumpEl) return;
  const j = jumpEl;
  j.style.transition = 'none';
  j.style.opacity = '0';
  j.style.transform = 'translate(-50%,-50%) scale(0.7)';
  // next frame: punch in
  requestAnimationFrame(() => {
    j.style.transition = 'opacity .14s ease-out, transform .55s cubic-bezier(.16,1,.3,1)';
    j.style.opacity = '1';
    j.style.transform = 'translate(-50%,-50%) scale(1.08)';
    // then ease away
    setTimeout(() => {
      j.style.transition = 'opacity .45s ease-in, transform .6s ease-in';
      j.style.opacity = '0';
      j.style.transform = 'translate(-50%,-50%) scale(1.22)';
    }, 360);
  });
}

// ── Settings button + panel ────────────────────────────────────────────────
function buildSettings(): void {
  // Gear button, top-right, themed glass.
  const btn = document.createElement('button');
  btn.setAttribute('aria-label', 'Settings');
  btn.innerHTML = gearSvg();
  btn.style.cssText = [
    'position:fixed',
    'top:calc(env(safe-area-inset-top, 0px) + 18px)',
    'right:calc(env(safe-area-inset-right, 0px) + 18px)',
    'z-index:50', 'width:42px', 'height:42px', 'padding:0',
    'display:flex', 'align-items:center', 'justify-content:center',
    'cursor:pointer', 'pointer-events:auto',
    'background:linear-gradient(180deg, rgba(9,16,28,0.40), rgba(9,16,28,0.66))',
    `border:1px solid rgba(255,179,64,0.45)`, 'border-radius:9px',
    '-webkit-backdrop-filter:blur(7px)', 'backdrop-filter:blur(7px)',
    `color:${ACCENT}`, 'transition:transform .25s ease, box-shadow .25s ease',
    'box-shadow:0 0 0 0 rgba(255,179,64,0)',
  ].join(';');
  btn.onpointerenter = () => { btn.style.boxShadow = '0 0 14px 1px rgba(255,179,64,0.35)'; };
  btn.onpointerleave = () => { btn.style.boxShadow = '0 0 0 0 rgba(255,179,64,0)'; };

  // Panel.
  const panel = document.createElement('div');
  panel.style.cssText = [
    'position:fixed',
    'top:calc(env(safe-area-inset-top, 0px) + 68px)',
    'right:calc(env(safe-area-inset-right, 0px) + 18px)',
    'z-index:50', 'width:228px', 'padding:16px 16px 15px',
    'pointer-events:auto', 'opacity:0', 'transform:translateY(-8px) scale(0.98)',
    'transform-origin:top right', 'transition:opacity .18s ease, transform .18s ease',
    'background:linear-gradient(180deg, rgba(9,16,28,0.62), rgba(9,16,28,0.80))',
    `border:1px solid rgba(255,179,64,0.28)`, `border-left:2px solid ${ACCENT}`,
    '-webkit-backdrop-filter:blur(9px)', 'backdrop-filter:blur(9px)',
    'clip-path:polygon(0 0, 100% 0, 100% calc(100% - 13px), calc(100% - 13px) 100%, 0 100%)',
    "font-family:'Avenir Next Condensed','SF Pro Display',system-ui,sans-serif",
    'color:#eaf4ff',
  ].join(';');
  panel.style.display = 'none';

  const title = document.createElement('div');
  title.textContent = 'Settings';
  title.style.cssText = `font-size:13px;letter-spacing:3.5px;text-transform:uppercase;color:#fff;margin-bottom:12px`;
  panel.appendChild(title);

  // Mute toggle row.
  const muteRow = settingRow('Sound', muteValueLabel());
  muteRow.btn.onclick = () => {
    muted = !muted;
    muteRow.val.textContent = muteValueLabel();
    muteRow.val.style.color = muted ? 'rgba(234,244,255,0.5)' : ACCENT_SOFT;
    window.dispatchEvent(new CustomEvent('sw-toggle-mute', { detail: { muted } }));
  };
  panel.appendChild(muteRow.btn);

  // Reset-save row.
  const resetRow = settingRow('Reset Save', 'Clear');
  resetRow.val.style.color = '#ff8a6a';
  resetRow.btn.onclick = () => {
    if (resetRow.btn.dataset.armed === '1') {
      try { localStorage.clear(); } catch { /* ignore */ }
      location.reload();
    } else {
      resetRow.btn.dataset.armed = '1';
      resetRow.val.textContent = 'Confirm?';
      setTimeout(() => {
        resetRow.btn.dataset.armed = '0';
        if (resetRow.val.textContent === 'Confirm?') resetRow.val.textContent = 'Clear';
      }, 2600);
    }
  };
  panel.appendChild(resetRow.btn);

  // Controls reminder.
  const divider = document.createElement('div');
  divider.style.cssText = 'height:1px;margin:11px 0 9px;background:linear-gradient(90deg, rgba(255,179,64,0.4), transparent)';
  panel.appendChild(divider);

  const ctrlTitle = document.createElement('div');
  ctrlTitle.textContent = 'Controls';
  ctrlTitle.style.cssText = 'font-size:10px;letter-spacing:2.5px;text-transform:uppercase;opacity:0.55;margin-bottom:7px';
  panel.appendChild(ctrlTitle);

  for (const [k, v] of [
    ['Drag', 'Steer ship'],
    ['Double-tap', 'Hyperdrive'],
    ['H', 'Jump now'],
    ['System edge', 'Auto-warp'],
  ] as [string, string][]) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;gap:16px;padding:2.5px 0';
    const key = document.createElement('span');
    key.textContent = k;
    key.style.cssText = 'font-size:10.5px;letter-spacing:1.5px;text-transform:uppercase;opacity:0.55';
    const desc = document.createElement('span');
    desc.textContent = v;
    desc.style.cssText = `font-size:11.5px;letter-spacing:0.5px;color:${ACCENT_SOFT}`;
    row.appendChild(key); row.appendChild(desc);
    panel.appendChild(row);
  }

  let open = false;
  const setOpen = (v: boolean) => {
    open = v;
    btn.style.transform = open ? 'rotate(45deg)' : 'rotate(0deg)';
    if (open) {
      panel.style.display = 'block';
      requestAnimationFrame(() => { panel.style.opacity = '1'; panel.style.transform = 'translateY(0) scale(1)'; });
    } else {
      panel.style.opacity = '0';
      panel.style.transform = 'translateY(-8px) scale(0.98)';
      setTimeout(() => { if (!open) panel.style.display = 'none'; }, 200);
    }
  };
  btn.onclick = (e) => { e.stopPropagation(); setOpen(!open); };
  // Tap outside closes the panel.
  window.addEventListener('pointerdown', (e) => {
    if (!open) return;
    const t = e.target as Node;
    if (!panel.contains(t) && !btn.contains(t)) setOpen(false);
  });

  document.body.appendChild(btn);
  document.body.appendChild(panel);
}

function muteValueLabel(): string { return muted ? 'Muted' : 'On'; }

function settingRow(label: string, value: string): { btn: HTMLButtonElement; val: HTMLSpanElement } {
  const btn = document.createElement('button');
  btn.style.cssText = [
    'display:flex', 'width:100%', 'justify-content:space-between', 'align-items:center',
    'gap:16px', 'padding:7px 0', 'margin:0', 'cursor:pointer', 'pointer-events:auto',
    'background:transparent', 'border:none', 'text-align:left',
    "font-family:inherit", 'color:inherit',
  ].join(';');
  const k = document.createElement('span');
  k.textContent = label;
  k.style.cssText = 'font-size:11.5px;letter-spacing:2px;text-transform:uppercase;opacity:0.7';
  const val = document.createElement('span');
  val.textContent = value;
  val.style.cssText = `font-size:12.5px;letter-spacing:1px;color:${ACCENT_SOFT}`;
  btn.appendChild(k); btn.appendChild(val);
  btn.onpointerenter = () => { k.style.opacity = '1'; };
  btn.onpointerleave = () => { k.style.opacity = '0.7'; };
  return { btn, val };
}

function gearSvg(): string {
  // Simple amber gear, currentColor.
  return `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3.2"/><path d="M12 2.2v2.4M12 19.4v2.4M21.8 12h-2.4M4.6 12H2.2M18.9 5.1l-1.7 1.7M6.8 17.2l-1.7 1.7M18.9 18.9l-1.7-1.7M6.8 6.8 5.1 5.1"/></svg>`;
}

// ── Public API ─────────────────────────────────────────────────────────────

/** Wire the juice into the live scene. Adds the streak field and builds the
 *  settings UI + JUMP label. Stores the camera's base FOV for the kick. */
export function init(scene: THREE.Scene, camera: THREE.PerspectiveCamera): void {
  camRef = camera;
  baseFov = camera.fov;
  buildStreaks();
  if (streakSeg) scene.add(streakSeg);
  buildJumpLabel();
  buildSettings();
}

/** Fire all warp juice: speed-streak light-tunnel, FOV kick, JUMP label.
 *  Call alongside the existing hyperdrive white flash. */
export function triggerWarp(): void {
  warpT = WARP_DUR;
  fovKick = 12; // degrees of FOV punch, eased back by update()
  flashJump();
}

/** Per-frame: advance the streak burst + ease the FOV kick back to base.
 *  Call once per render frame from tick() with the frame delta. */
export function update(dt: number): void {
  if (warpT > 0) {
    warpT = Math.max(0, warpT - dt);
    updateStreaks();
  } else if (streakSeg && streakSeg.visible) {
    streakSeg.visible = false;
  }
  if (camRef) {
    // Critically-damped-ish exponential ease of the FOV bump back to 0.
    if (fovKick > 0.01) {
      fovKick *= Math.pow(0.0001, dt); // fast decay, frame-rate independent
      if (fovKick < 0.01) fovKick = 0;
      camRef.fov = baseFov + fovKick;
      camRef.updateProjectionMatrix();
    } else if (fovKick !== 0) {
      fovKick = 0;
      camRef.fov = baseFov;
      camRef.updateProjectionMatrix();
    }
  }
}
